import { AVATAR_COLORS } from './communityConstants'

// MIRROR of trybe-mobile client/scripts/common/communityUtils.js (the subset the
// dashboard needs). Keep the logic identical to mobile — a post written by the
// dashboard must be byte-for-byte the same shape as one written by the app, and
// avatar colors/initials must resolve the same on both surfaces.

export const getCommunityPath = (coachOrgId) => `community/${coachOrgId}`
export const getFeedPath = (coachOrgId, feedId) => `community/${coachOrgId}/feeds/${feedId}`

// First letter or digit of a word, or '' when the word has none (an emoji-only
// word like the flexed arm in "LeoMoves 💪"). Iterates by code point instead of
// indexing the string: an emoji is two UTF-16 units, and taking word[0] would
// return half of it, which renders as a broken-glyph box in the avatar.
const getWordInitial = (word) => Array.from(word).find((char) => /[\p{L}\p{N}]/u.test(char)) || ''

export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?'
  const initials = name.trim().split(/\s+/).map(getWordInitial).filter(Boolean)
  if (initials.length === 0) return '?'
  const first = initials[0]
  const last = initials.length > 1 ? initials[initials.length - 1] : ''
  return (first + last).toUpperCase() || '?'
}

// Auth0 fabricates fallback avatars (initials PNGs from the account email) at
// cdn.auth0.com/avatars, often wrapped in a gravatar URL's d= param. They look
// like initials but show the wrong letters once someone sets a community name —
// never treat them as a real photo. Real social photos come from other hosts.
export const isDefaultProviderAvatar = (url) =>
  typeof url === 'string' && (url.includes('cdn.auth0.com') || url.includes('cdn.auth0.com'.replace(/\//g, '%2F')))

export const getUsablePicture = (url) => (url && !isDefaultProviderAvatar(url) ? url : null)

export const getAvatarColor = (userId) => {
  if (!userId || typeof userId !== 'string') return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) % 100000
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export const normalizeCommunityRecords = (records) => {
  if (!records || typeof records !== 'object') {
    return {}
  }

  const normalized = {}
  Object.entries(records).forEach(([firebaseKey, record]) => {
    if (!record || typeof record !== 'object') {
      return
    }
    // Key strictly by the firebase key — it's canonical and unique. The client
    // writes id === key anyway, and trusting an inner id would let a record
    // with a duplicated id mask another record.
    normalized[firebaseKey] = { ...record, id: firebaseKey }
  })
  return normalized
}

// Denormalized author display fields stamped onto posts/replies (RTDB has no joins;
// this keeps the feed render one listener, no per-author fetch waterfall).
export const buildAuthorFields = ({ userId, displayName, picture, isCoach }) => ({
  authorName: displayName || '',
  authorColor: getAvatarColor(userId),
  authorPicture: getUsablePicture(picture),
  authorIsCoach: isCoach === true,
})

export const buildNewPostWrites = ({
  coachOrgId,
  feedId,
  postId,
  authorId,
  author,
  text,
  createdAt,
  notifyMembers,
  pinnedUntil,
  repliesOff,
}) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  return {
    [`${feedPath}/posts/${postId}`]: {
      id: postId,
      authorId,
      text,
      createdAt,
      replyCount: 0,
      likeCount: 0,
      ...buildAuthorFields({ userId: authorId, ...author }),
      // Activity stamps power the dashboard's "Needs reply" coach inbox: a thread
      // needs attention when the member spoke last (see needsCoachReply).
      ...(author?.isCoach === true ? { lastCoachActivityAt: createdAt } : { lastMemberActivityAt: createdAt }),
      // Coach-only fields (gated upstream in the action):
      // notifyMembers — the cloud function broadcasts the post to all members.
      // pinned/pinnedUntil — shows in the feed's Pinned area until the date.
      // repliesOff — replies are refused by rules and the reply box is hidden.
      ...(notifyMembers === true ? { notifyMembers: true } : {}),
      ...(typeof pinnedUntil === 'number' ? { pinned: true, pinnedUntil } : {}),
      ...(repliesOff === true ? { repliesOff: true } : {}),
    },
  }
}

export const buildNewReplyWrites = ({
  coachOrgId,
  feedId,
  postId,
  postAuthorId,
  replyId,
  activityId,
  authorId,
  author,
  text,
  createdAt,
  inc,
}) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  const update = {
    [`${feedPath}/replies/${postId}/${replyId}`]: {
      id: replyId,
      postId,
      authorId,
      text,
      createdAt,
      likeCount: 0,
      ...buildAuthorFields({ userId: authorId, ...author }),
    },
    [`${feedPath}/posts/${postId}/replyCount`]: inc(1),
    // Stamp which side just spoke — powers the "New" reply highlight.
    [`${feedPath}/posts/${postId}/${author?.isCoach === true ? 'lastCoachActivityAt' : 'lastMemberActivityAt'}`]:
      createdAt,
  }

  // A coach's reply answers the thread, so it clears the needs-reply flag in
  // the same atomic write. That makes the Needs reply queue update the moment
  // the reply sends, instead of waiting on the cloud function (which also
  // clears the flag — clearing twice is harmless). Member replies must not
  // include this path: the rules only let org admins write it.
  if (author?.isCoach === true) {
    update[`${feedPath}/posts/${postId}/needsReplySince`] = null
  }

  if (postAuthorId && postAuthorId !== authorId && activityId) {
    update[`${feedPath}/activity/${postAuthorId}/${activityId}`] = {
      id: activityId,
      type: 'reply',
      postId,
      replyId,
      actorId: authorId,
      actorName: author?.displayName || '',
      createdAt,
    }
  }

  return update
}

export const buildLikeOrUnlikePostWrites = ({
  coachOrgId,
  feedId,
  postId,
  postAuthorId,
  activityId,
  userId,
  actorName,
  isLiked,
  createdAt,
  inc,
}) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  const update = {
    [`${feedPath}/userLikes/${userId}/posts/${postId}`]: isLiked ? null : createdAt,
    [`${feedPath}/posts/${postId}/likeCount`]: inc(isLiked ? -1 : 1),
  }

  if (!isLiked && postAuthorId && postAuthorId !== userId && activityId) {
    update[`${feedPath}/activity/${postAuthorId}/${activityId}`] = {
      id: activityId,
      type: 'like',
      postId,
      actorId: userId,
      actorName: actorName || '',
      createdAt,
    }
  }

  return update
}

export const buildLikeOrUnlikeReplyWrites = ({
  coachOrgId,
  feedId,
  postId,
  replyId,
  replyAuthorId,
  activityId,
  userId,
  actorName,
  isLiked,
  createdAt,
  inc,
}) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  const update = {
    [`${feedPath}/userLikes/${userId}/replies/${postId}/${replyId}`]: isLiked ? null : createdAt,
    [`${feedPath}/replies/${postId}/${replyId}/likeCount`]: inc(isLiked ? -1 : 1),
  }

  if (!isLiked && replyAuthorId && replyAuthorId !== userId && activityId) {
    update[`${feedPath}/activity/${replyAuthorId}/${activityId}`] = {
      id: activityId,
      type: 'like',
      postId,
      replyId,
      actorId: userId,
      actorName: actorName || '',
      createdAt,
    }
  }

  return update
}

// A thread needs the coach's attention while its server-maintained flag exists.
// The cloud functions set needsReplySince on member posts/replies and clear it
// on any admin reply; "Mark as done" clears it by hand (admin-only rule), so
// members can't tamper with the queue and clients never have to compute it.
export const needsCoachReply = (post) => Boolean(post) && typeof post.needsReplySince === 'number'

// Keeps tab pills from growing with the number.
export const formatBadgeCount = (count) => (count > 99 ? '99+' : `${count}`)

export const buildPinPostWrites = ({ coachOrgId, feedId, postId, pinnedUntil }) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  const isPinning = typeof pinnedUntil === 'number'
  return {
    [`${feedPath}/posts/${postId}/pinned`]: isPinning ? true : null,
    [`${feedPath}/posts/${postId}/pinnedUntil`]: isPinning ? pinnedUntil : null,
  }
}

// Hides posts (or replies) whose author the coach has banned, or whom the
// viewer has blocked. The dashboard has no per-user blocks, so it passes an
// empty map there; the signature is kept identical to mobile's so the two
// surfaces hide exactly the same records.
export const filterVisiblePosts = (posts = [], blockedUserIds = {}, bannedMemberIds = {}) =>
  posts.filter((post) => !blockedUserIds[post.authorId] && !bannedMemberIds[post.authorId])

export const isPostPinned = (post, currentTime) =>
  Boolean(post && post.pinned === true && typeof post.pinnedUntil === 'number' && post.pinnedUntil > currentTime)

export const buildDeletePostWrites = ({ coachOrgId, feedId, postId }) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  return {
    [`${feedPath}/posts/${postId}`]: null,
    [`${feedPath}/replies/${postId}`]: null,
  }
}

export const buildDeleteReplyWrites = ({ coachOrgId, feedId, postId, replyId, inc }) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  return {
    [`${feedPath}/replies/${postId}/${replyId}`]: null,
    [`${feedPath}/posts/${postId}/replyCount`]: inc(-1),
  }
}

// Fallback when the rules refuse the decrement because replyCount is already at zero.
export const buildDeleteReplyOnlyWrites = ({ coachOrgId, feedId, postId, replyId }) => {
  const feedPath = getFeedPath(coachOrgId, feedId)
  return {
    [`${feedPath}/replies/${postId}/${replyId}`]: null,
  }
}

export const isPermissionDeniedError = (error) =>
  error?.code === 'database/permission-denied' || /permission[ _-]?denied/i.test(error?.message || '')
